<script>
// @ts-check
import _chunk from 'lodash/chunk'
import { mapMutations } from 'vuex'
import { Maybe } from '@domain'
import ChartIntroComponents from '@components/chartIntros'

let chartIconMap = {
  Attendance: 'txoc_site_icon_persons',
  'STAAR Outcomes': 'STAAR_Outcomes',
  'TSIA Data': 'TSIA_Data',
  'Recommended/ Distinguished Achievement Graduates': 'Rec_Dist_Achievement',
  'Advanced/Dual Courses Taken': 'Advanced_Dual_Courses',
  'College Enrollment': 'laptop-icon',
  'College Readiness': 'College_Readiness',
  'College Persistence Rates': 'stairs-icon',
  'Summer Melt Rates': 'book-icon',
  '4-year college application rates': 'txoc_site_icon_building_cap',
  '4-year college admission rates': 'compass-icon',
  'FAFSA submission': 'txoc_site_icon_tools_smartphone_hand',
  'FAFSA completion': 'txoc_site_icon_laptop',
  'SAT/ACT Participation': 'SAT_ACT_Participation',
  'Industry-based certification completion': 'txoc_site_icon_reward_diploma',
  'CTE concentration rates': 'compass-icon',
  '3- and 6- year credential attainment rates': 'stairs-icon',
  'Employment rate': 'txoc_site_icon_jobs',
  'Median Earnings': 'cash-icon',
}

let chartIntroCompMap = {
  'College Readiness': 'chart-v3-intro',
  'Advanced/Dual Courses Taken': 'chart-v4-intro',
  'SAT/ACT Participation': 'chart-v5-intro',
  'Recommended/ Distinguished Achievement Graduates': 'chart-v6-intro',
  Attendance: 'chart-v7-intro',
  'STAAR Outcomes': 'chart-v8-intro',
  'TSIA Data': 'chart-v9-intro',
}

export default {
  components: {
    ...ChartIntroComponents,
  },
  props: {
    type: { type: String, default: '' },
    subType: {
      type: Array,
      default: function() {
        return []
      },
    },
    color: { type: String, default: '' },
  },
  computed: {
    subTypeChunk: function() {
      return _chunk(this.subType, 4)
    },
  },
  methods: {
    ...mapMutations({ setChartType: 'wizard/SetChartType' }),
    select(chartType) {
      this.setChartType(Maybe.of(chartType))
    },
    iconClass(chartName) {
      return chartIconMap[chartName] || ''
    },
    chartIntroComponentName(chartType) {
      return chartIntroCompMap[chartType] || 'empty-intro'
    },
  },
}
</script>

<template>
  <v-layout row wrap align-center>
    <v-flex class="tile-type mt-5 mb-2" tag="span" xs12>{{ type }}</v-flex>
    <v-flex>
      <v-layout v-for="j in subTypeChunk.length" :key="j" mt-3 row>
        <v-flex
          v-for="([displayName, ctype],i) in subTypeChunk[j-1]"
          :key="i"
          d-inline-flex
          xs3
          pr-3
          @click="select(ctype)"
        >
          <div class="fix-card-ratio">
            <v-card
              :class="[iconClass(displayName)]"
              height="100%"
              width="100%"
              hover
              to="/dataViz/wizard"
            >
              <div class="chart-tooltip">
                <v-btn class="chart-tooltip-activator" :aria-label="displayName" icon small>
                  <v-icon>info_outline</v-icon>
                </v-btn>
                <div class="tooltip-text">
                  <component :is="chartIntroComponentName(displayName)"/>
                </div>
              </div>
              <div class="card-icon">
                <div :class="[iconClass(displayName)]"/>
              </div>
              <v-card-text :class="[iconClass(displayName)]" v-text="displayName"/>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<style lang="scss" scoped>
.tile-type {
  font-size: 24px;
}

.fix-card-ratio {
  width: 100%;
  padding-bottom: 70%;
  position: relative;

  > .card {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: inline-flex;
    flex-flow: column nowrap;
    align-items: stretch;

    &:hover {
      box-shadow: 5px 10px 10px;
    }
    &::before {
      content: '';
      flex: 13 13 0;
    }

    .chart-tooltip {
      position: absolute;
      right: 0;
      top: 0;

      .tooltip-text {
        display: none;
        position: absolute;
        top: 90%;
        right: 5px;
        background-color: white;
        z-index: 100;
        opacity: 0;
        transition: opacity 0.5s ease-out;
      }

      &:hover .tooltip-text {
        opacity: 0.95;
        display: block;
      }
    }
    .chart-tooltip-activator {
      color: white;
      transform: scale(0.85, 0.85);
    }

    > .card__text {
      color: white;
      font-weight: bold;
      font-size: 16px;
      font-size: 1vw;
      line-height: 130%;
      text-align: center;
      padding-top: 8px;
      padding-bottom: 1px;
      flex: 34 34 0;
    }

    > .card__text.Rec_Dist_Achievement {
      padding-bottom: 6px !important;
    }

    $img_w: 4rem;
    $icon_name: 'txoc_site_icon_building_cap';
    > .card-icon {
      display: flex;
      flex-direction: row;
      flex: 29 29 0;

      &::before,
      &::after {
        content: '';
        flex: 16 16 0;
      }

      $icon_names: [ 'txoc_site_icon_persons', 'STAAR_Outcomes', 'TSIA_Data',
        'Rec_Dist_Achievement', 'Advanced_Dual_Courses', 'laptop-icon',
        'College_Readiness', 'txoc_site_icon_building_cap', 'compass-icon',
        'txoc_site_icon_tools_smartphone_hand', 'txoc_site_icon_laptop',
        'txoc_site_icon_reward_diploma', 'txoc_site_icon_jobs', 'cash-icon',
        'SAT_ACT_Participation' ];
      @each $i in $icon_names {
        & > .#{$i} {
          flex: 11 11 0;
          background: url('../assets/images/#{$i}.png') center no-repeat;
          background-size: 95%;
        }
      }

      & > .STAAR_Outcomes {
        background-size: 67% !important;
      }

      & > .SAT_ACT_Participation {
        background-size: 90% !important;
      }
    }
  }
  > .card.Rec_Dist_Achievement,
  .card.TSIA_Data,
  .card.STAAR_Outcomes,
  .card.txoc_site_icon_persons,
  .card.Advanced_Dual_Courses {
    background: linear-gradient(to right, #11a0b0, #4bc2c7);
  }

  > .card.SAT_ACT_Participation,
  .card.College_Readiness {
    background: linear-gradient(to left, #57c170, #3ba756);
  }
}
</style>
